import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Button,
  IconButton,
  Link,
  Tooltip,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@material-ui/core";
import Iconify from "src/components/iconify";
import EvaCheckmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import EvaArrowIosUpwardFill from "@iconify/icons-eva/arrow-ios-upward-fill";
import EvaArrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import EvaEdit2Fill from "@iconify/icons-eva/edit-2-fill";
import EvaTrash2Fill from "@iconify/icons-eva/trash-2-fill";
import EvaPlusFill from "@iconify/icons-eva/plus-fill";
import PropTypes from "prop-types";
import { empleadosApi } from "src/services/empleadosApi";
import VoiceInputButton from "../../VoiceInputButton";
import AICorrectButton from "../../AICorrectButton";
import { generarUuid } from "../../EditarPropuestaV2/utils/generarUuid";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { BRAND } from "src/theme/brandTokens";

const MicrocopyFoto = ({ fotoUrl, authRut, miembroRut }) => {
  const esSelf = authRut && miembroRut && authRut === miembroRut;

  if (!fotoUrl) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin foto cargada.{" "}
        {esSelf ? (
          <>
            <Link
              component={RouterLink}
              to={`/dashboard/empleados/${authRut}`}
              underline="hover"
            >
              {"Agrega tu foto en tu perfil ->"}
            </Link>
          </>
        ) : (
          <>Pídele al empleado que la agregue en su perfil.</>
        )}
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary">
      Esta foto se toma del perfil del empleado.{" "}
      {esSelf ? (
        <>
          Para cambiarla,{" "}
          <Link
            component={RouterLink}
            to={`/dashboard/empleados/${authRut}`}
            underline="hover"
          >
            {"edita tu perfil ->"}
          </Link>
        </>
      ) : (
        <>Para cambiarla, pídele al empleado que la actualice en "Mi perfil".</>
      )}
    </Typography>
  );
};

MicrocopyFoto.propTypes = {
  fotoUrl: PropTypes.string,
  authRut: PropTypes.string,
  miembroRut: PropTypes.string,
};

export const EQUIPO_MIEMBRO_INICIAL = {
  orden: 0,
  rolDisplay: "",
  nombre: "",
  fotoUrl: "",
  descripcionExperiencia: "",
  refEmpleado: null,
};

// ─── Dialog de edición de miembro ────────────────────────────────────────────

const MiembroEditDialog = ({
  open,
  onClose,
  miembro,
  onSave,
  empleados,
  sucursal,
  isNew,
}) => {
  const auth = useSelector((state) => state.auth.user);
  const [local, setLocal] = useState({ ...EQUIPO_MIEMBRO_INICIAL });

  useEffect(() => {
    if (open) setLocal({ ...EQUIPO_MIEMBRO_INICIAL, ...miembro });
  }, [open, miembro]);

  const set = (field, value) =>
    setLocal((prev) => ({ ...prev, [field]: value }));

  const handleSeleccionarEmpleado = (emp) => {
    if (!emp) return;
    setLocal((prev) => ({
      ...prev,
      nombre: `${emp.nombre} ${emp.apellidos}`.trim(),
      fotoUrl: emp.foto_url || prev.fotoUrl,
      descripcionExperiencia: emp.bio || prev.descripcionExperiencia,
      rolDisplay: emp.cargo || prev.rolDisplay,
      refEmpleado: emp.rut,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {isNew ? "Agregar miembro" : "Editar miembro"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          {/* Avatar preview + foto URL */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={local.fotoUrl}
              sx={{
                width: 56,
                height: 56,
                bgcolor: BRAND.color.primary,
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              {local.nombre ? local.nombre.charAt(0).toUpperCase() : "?"}
            </Avatar>
            <MicrocopyFoto
              fotoUrl={local.fotoUrl}
              authRut={auth?.empleado?.rut}
              miembroRut={local?.refEmpleado}
            />
          </Box>

          {/* Selector empleado existente (auto-fill) */}
          {sucursal && empleados.length > 0 && (
            <Autocomplete
              options={empleados}
              getOptionLabel={(e) => (e ? `${e.nombre} ${e.apellidos}` : "")}
              value={null}
              onChange={(_, emp) => handleSeleccionarEmpleado(emp)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Importar datos de empleado"
                  placeholder="Busca para autocompletar nombre, foto y bio..."
                  size="small"
                  helperText="Opcional — rellena los campos automáticamente"
                />
              )}
            />
          )}

          <Divider />

          {/* Nombre */}
          <Box>
            <TextField
              fullWidth
              label="Nombre completo *"
              value={local.nombre}
              onChange={(e) => set("nombre", e.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <AICorrectButton
                text={local.nombre}
                context="Nombre completo de un profesional que aparece en el equipo de una propuesta comercial tributaria."
                onCorrected={(t) => set("nombre", t)}
                disabled={!local.nombre}
              />
            </Box>
          </Box>

          {/* Rol */}
          <Box>
            <TextField
              fullWidth
              label="Rol de presentación *"
              placeholder="Ej: Socio Director, Gerente de Proyectos"
              value={local.rolDisplay}
              onChange={(e) => set("rolDisplay", e.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <AICorrectButton
                text={local.rolDisplay}
                context="Rol de presentación de un profesional en el equipo de una propuesta comercial tributaria."
                onCorrected={(t) => set("rolDisplay", t)}
                disabled={!local.rolDisplay}
              />
            </Box>
          </Box>

          {/* Descripción */}
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descripción / experiencia"
              value={local.descripcionExperiencia}
              onChange={(e) => set("descripcionExperiencia", e.target.value)}
              helperText="Aparece en la sección de equipo del PDF."
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <VoiceInputButton
                onTranscript={(txt) =>
                  set(
                    "descripcionExperiencia",
                    local.descripcionExperiencia
                      ? `${local.descripcionExperiencia} ${txt}`
                      : txt,
                  )
                }
              />
              <AICorrectButton
                text={local.descripcionExperiencia}
                context="Descripción de experiencia de un profesional en el equipo de una propuesta comercial tributaria. Destacar logros y especialidades relevantes."
                onCorrected={(t) => set("descripcionExperiencia", t)}
                disabled={!local.descripcionExperiencia}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!local.nombre.trim() || !local.rolDisplay.trim()}
          onClick={() => {
            onSave(local);
            // No llamar onClose() — el padre cierra el dialog desde su handleGuardar
          }}
          sx={{
            bgcolor: BRAND.color.primary,
            "&:hover": { bgcolor: BRAND.color.primaryDark },
          }}
        >
          {isNew ? "Agregar miembro" : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MiembroEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  miembro: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  empleados: PropTypes.array,
  sucursal: PropTypes.string,
  isNew: PropTypes.bool,
};

// ─── Step principal ───────────────────────────────────────────────────────────

/**
 * Paso 6 — Equipo de Trabajo
 *
 * Lista compacta de miembros con edición en Dialog.
 * Auto-sugiere vendedor + encargados únicos de los ítems al entrar vacío.
 */
const StepEquipo = ({ formik }) => {
  const sucursal = formik.values.sucursal;
  const equipo = formik.values.equipo;

  const [triggerEmpleados, { data: empleados = [], isFetching }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  useEffect(() => {
    if (sucursal) triggerEmpleados({ id: sucursal, estado: "ALL" });
  }, [sucursal, triggerEmpleados]);

  // Auto-sugerir equipo cuando está vacío
  useEffect(() => {
    if (equipo.length > 0 || empleados.length === 0) return;

    const sugeridos = [];
    const rutsSugeridos = new Set();

    const vendedorRut = formik.values.vendedor;
    if (vendedorRut) {
      const empVendedor = empleados.find((e) => e.rut === vendedorRut);
      if (empVendedor && !rutsSugeridos.has(empVendedor.rut)) {
        rutsSugeridos.add(empVendedor.rut);
        sugeridos.push({
          orden: sugeridos.length,
          rolDisplay: empVendedor.cargo || "Vendedor",
          nombre: `${empVendedor.nombre} ${empVendedor.apellidos}`.trim(),
          fotoUrl: empVendedor.foto_url || "",
          descripcionExperiencia: empVendedor.bio || "",
          refEmpleado: empVendedor.rut,
        });
      }
    }

    const encargadosRuts = [
      ...new Set(
        formik.values.items.map((it) => it.refEmpleado).filter(Boolean),
      ),
    ];
    for (const rut of encargadosRuts) {
      if (rutsSugeridos.has(rut)) continue;
      const emp = empleados.find((e) => e.rut === rut);
      if (emp) {
        rutsSugeridos.add(emp.rut);
        sugeridos.push({
          orden: sugeridos.length,
          rolDisplay: emp.cargo || "Encargado",
          nombre: `${emp.nombre} ${emp.apellidos}`.trim(),
          fotoUrl: emp.foto_url || "",
          descripcionExperiencia: emp.bio || "",
          refEmpleado: emp.rut,
        });
      }
    }

    if (sugeridos.length > 0) formik.setFieldValue("equipo", sugeridos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empleados]);

  // ── Dialog state ────────────────────────────────────────────────────────────
  const [dialogIndex, setDialogIndex] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const abrirNuevo = () => {
    formik.setFieldValue("equipo", [
      ...equipo,
      { ...EQUIPO_MIEMBRO_INICIAL, orden: equipo.length, __key: generarUuid() },
    ]);
    setDialogIndex(equipo.length);
    setIsNew(true);
  };

  const abrirEditar = (index) => {
    setDialogIndex(index);
    setIsNew(false);
  };

  const handleCerrar = () => {
    if (isNew && dialogIndex === equipo.length - 1) {
      formik.setFieldValue("equipo", equipo.slice(0, -1));
    }
    setDialogIndex(null);
    setIsNew(false);
  };

  const handleGuardar = (miembroEditado) => {
    const nuevos = equipo.map((m, i) =>
      i === dialogIndex ? miembroEditado : m,
    );
    formik.setFieldValue("equipo", nuevos);
    setDialogIndex(null);
    setIsNew(false);
  };

  const handleEliminar = (index) => {
    formik.setFieldValue(
      "equipo",
      equipo.filter((_, i) => i !== index).map((m, i) => ({ ...m, orden: i })),
    );
  };

  const moverArriba = (index) => {
    if (index === 0) return;
    const arr = [...equipo];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    formik.setFieldValue(
      "equipo",
      arr.map((m, i) => ({ ...m, orden: i })),
    );
  };

  const moverAbajo = (index) => {
    if (index === equipo.length - 1) return;
    const arr = [...equipo];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    formik.setFieldValue(
      "equipo",
      arr.map((m, i) => ({ ...m, orden: i })),
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Equipo de Trabajo
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Define el equipo que aparecerá en el PDF. Es un snapshot: cambios
        futuros en perfiles de empleados no afectarán esta propuesta.
      </Typography>

      {/* ── Vendedor / Supervisor ────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={empleados}
            getOptionLabel={(e) => (e ? `${e.nombre} ${e.apellidos}` : "")}
            value={
              empleados.find((e) => e.rut === formik.values.vendedor) || null
            }
            onChange={(_, emp) => {
              formik.setFieldValue("vendedor", emp?.rut ?? "");
              formik.setFieldValue("vendedorCorreo", emp?.correo ?? "");
              formik.setFieldValue("vendedorTelefono", emp?.telefono ?? "");
            }}
            loading={isFetching}
            disabled={!sucursal}
            noOptionsText={
              sucursal
                ? "Sin empleados disponibles"
                : "Selecciona sucursal primero"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendedor *"
                placeholder="Selecciona un vendedor"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={empleados}
            getOptionLabel={(e) => (e ? `${e.nombre} ${e.apellidos}` : "")}
            value={
              empleados.find((e) => e.rut === formik.values.supervisor) || null
            }
            onChange={(_, emp) =>
              formik.setFieldValue("supervisor", emp?.rut ?? "")
            }
            loading={isFetching}
            disabled={!sucursal}
            noOptionsText={
              sucursal
                ? "Sin empleados disponibles"
                : "Selecciona sucursal primero"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supervisor (opcional)"
                placeholder="Selecciona un supervisor"
              />
            )}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }}>
        <Typography variant="caption" color="textSecondary">
          Miembros en el PDF
        </Typography>
      </Divider>

      {/* ── Lista de miembros ────────────────────────────────────────────── */}
      {equipo.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            borderStyle: "dashed",
            borderColor: "grey.300",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {empleados.length > 0
              ? 'Cargando sugerencias automáticas… o haz clic en "Agregar miembro".'
              : 'Sin miembros. Haz clic en "Agregar miembro" para comenzar.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mb: 2 }}>
          {equipo.map((miembro, index) => (
            <Paper
              key={miembro.__key ?? index}
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.25,
                mb: 1,
                cursor: "pointer",
                transition: "box-shadow 0.15s",
                "&:hover": { boxShadow: 2 },
              }}
              onClick={() => abrirEditar(index)}
            >
              {/* Avatar */}
              <Avatar
                src={miembro.fotoUrl}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: BRAND.color.primary,
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                {miembro.nombre ? miembro.nombre.charAt(0).toUpperCase() : "?"}
              </Avatar>

              {/* Nombre + rol */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {miembro.nombre || (
                    <em style={{ color: "#999" }}>Sin nombre</em>
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary" noWrap>
                  {miembro.rolDisplay || "Sin rol"}
                </Typography>
              </Box>

              {/* Indicador de descripción */}
              {miembro.descripcionExperiencia && (
                <Tooltip title="Tiene descripción de experiencia">
                  <span>
                    <Iconify
                      icon={EvaCheckmarkCircle2Fill}
                      width={16}
                      height={16}
                      style={{ color: "#4CAF50", flexShrink: 0 }}
                    />
                  </span>
                </Tooltip>
              )}

              {/* Reordenar */}
              <Box
                sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  size="small"
                  onClick={() => moverArriba(index)}
                  disabled={index === 0}
                  sx={{ p: 0.25 }}
                >
                  <Iconify
                    icon={EvaArrowIosUpwardFill}
                    width={16}
                    height={16}
                  />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moverAbajo(index)}
                  disabled={index === equipo.length - 1}
                  sx={{ p: 0.25 }}
                >
                  <Iconify
                    icon={EvaArrowIosDownwardFill}
                    width={16}
                    height={16}
                  />
                </IconButton>
              </Box>

              {/* Editar */}
              <Tooltip title="Editar miembro">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirEditar(index);
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  <Iconify icon={EvaEdit2Fill} width={18} height={18} />
                </IconButton>
              </Tooltip>

              {/* Eliminar */}
              <Tooltip title="Eliminar miembro">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEliminar(index);
                  }}
                  sx={{ flexShrink: 0, color: "error.main" }}
                >
                  <Iconify icon={EvaTrash2Fill} width={18} height={18} />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      {/* Botón agregar */}
      <Button
        variant="outlined"
        startIcon={<Iconify icon={EvaPlusFill} width={18} height={18} />}
        onClick={abrirNuevo}
        sx={{
          borderColor: BRAND.color.primary,
          color: BRAND.color.primary,
          "&:hover": {
            borderColor: BRAND.color.primaryDark,
            color: BRAND.color.primaryDark,
          },
        }}
      >
        Agregar miembro
      </Button>

      {/* Dialog de edición */}
      {dialogIndex !== null && dialogIndex < equipo.length && (
        <MiembroEditDialog
          open
          onClose={handleCerrar}
          miembro={equipo[dialogIndex]}
          onSave={handleGuardar}
          empleados={empleados}
          sucursal={sucursal}
          isNew={isNew}
        />
      )}
    </Box>
  );
};

StepEquipo.propTypes = {
  formik: PropTypes.object.isRequired,
};

export default StepEquipo;
